import React from 'react';
import { connect } from 'react-redux';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { IValidationState } from '../../../mixins/validation';
import { fetchDelete, fetchGet, fetchPost } from '../../../store/fetch';
import { AssetTypes } from '../../../utils/enums/AssetTypes';
import { IConnectProps } from '../../../utils/IConnectProps';
import { Panel, PanelBody, PanelHorizontalSpacing, Tray } from '../../elements';
import { Component } from '../../utils/Component';
import { DisplayMessage } from '../../utils/displayMessage';
import { DocumentsList } from '../../utils/DocumentsList';
import { AssetDropzone } from '../Common/AssetDropzone';

interface ISurveyEditDownloadAndDocumentsProps extends ITranslateProps, IConnectProps {
    userToken: string;
    surveyId: string;
    objectId: string;
    canAddImages: boolean;
}

interface IAsset {
    date: Date;
    type: string;
    size: number;
    name: string;
    id: string;
    labels: string[];
}

interface ISurveyEditDownloadAndDocumentsState extends IValidationState {
    objectId?: undefined | string;
    objectName?: undefined | string;
    assets: IAsset[];
    isUploading?: undefined | boolean;
    assetIdEditName?: undefined | string;
}

function mapStateToProps(state: any) {
    const { authentication } = state;
    return {
        userToken: authentication.token,
    };
}

class SurveyEditDownloadAndDocuments extends Component<ISurveyEditDownloadAndDocumentsProps, ISurveyEditDownloadAndDocumentsState> {
    constructor(props: ISurveyEditDownloadAndDocumentsProps) {
        super(props);

        this.state = {
            submitting: false,
            failed: false,
            saved: false,
            objectId: this.props.objectId,
            assets: [],
        };
    }

    public override componentDidMount() {
        void this.load();
    }

    public override render() {
        return (
            <Tray>
                {this.renderMessage()}
                <Panel>
                    <PanelBody>
                        <PanelHorizontalSpacing>
                            <AssetDropzone
                                acceptedFiles={
                                    this.props.canAddImages
                                        ? '.doc,.docx,.txt,.xls,.xlsx,.pdf,.zip,.tar,.dwg,.ifc,.png,.jpg,.jpeg,.tif,.tiff'
                                        : '.doc,.docx,.txt,.xls,.xlsx,.pdf,.zip,.tar,.dwg,.ifc,.tif,.tiff'
                                }
                                onComplete={this.onDropzoneComplete.bind(this)}
                                onError={this.onDropzoneError.bind(this)}
                                objectId={this.state.objectId}
                                surveyId={this.props.surveyId}
                                onSending={async () => this.setState({ isUploading: true })}
                                onQueueComplete={async () => this.setState({ isUploading: false })}
                                autoProcessQueue={true}
                                maxFileSizeMb={45_000}
                            />
                        </PanelHorizontalSpacing>
                        <PanelBody noPadding={true}>
                            <DocumentsList
                                assets={this.state.assets}
                                surveyId={this.props.surveyId}
                                blueHeader={true}
                                onRemove={async (assetId: string) => this.remove(assetId)}
                                onUpdate={this.saveAssetName.bind(this)}
                                manageLabels
                            />
                        </PanelBody>
                    </PanelBody>
                </Panel>
            </Tray>
        );
    }

    private async saveAssetName(assetId: string, data: Record<string, unknown>) {
        const url = `/api/surveys/${this.props.surveyId}/assets/${assetId}`;
        await fetchPost(url, {}, data);
        await this.setState({ assetIdEditName: undefined });
        await this.loadAssets();
    }

    private async onDropzoneError(errorCode: string) {
        await this.setState({ submitting: false, errorCode, failed: true });
    }

    private async onDropzoneComplete() {
        await this.loadAssets();
    }

    private async load() {
        await this.setState({ submitting: true });
        const url = `/api/surveys/${this.props.surveyId}`;
        const result = await fetchGet(url);
        result.json.surveryedOn = new Date(result.json.surveryedOn);
        await this.setState(result.json);
        await this.loadAssets();
        await this.loadObject(result.json.objectId);
        await this.setState({ submitting: false });
    }

    private async loadAssets() {
        const url = '/api/assets';
        let types = `${AssetTypes.Document},${AssetTypes['3DModel']},${AssetTypes.MeasurementXLS},${AssetTypes.MeasurementPDF},${AssetTypes.Text},${AssetTypes.BIM},${AssetTypes.ZipArchive},${AssetTypes.TarArchive},${AssetTypes.AutoCad},${AssetTypes.ImageTif}`;
        if (this.props.canAddImages) {
            types += `,${AssetTypes.Image},${AssetTypes.Image360}`;
        }

        const queryString = {
            surveyId: this.props.surveyId,
            types,
            unassigned: true,
        };
        const result = await fetchGet(url, queryString, ['404']);
        await this.setState({ submitting: false, assets: result.json });
    }

    private async loadObject(objectId: string) {
        await this.setState({ submitting: true });
        const url = `/api/objects/${objectId}?fields=name`;
        const result = await fetchGet(url);
        await this.setState(result.json);
        await this.setState({ submitting: false, objectName: result.json.name });
    }

    private renderMessage(): JSX.Element {
        if (this.state && this.state.message) {
            return <DisplayMessage message={this.state.message} messageType="general" />;
        } else if (this.state.failed) {
            let message: string;

            switch (this.state.errorCode) {
                case 'STORAGE_LIMIT_REACHED':
                    message = this.props.t('asset.errorStorageLimitReached');
                    break;
                case 'FILE_TOO_BIG':
                    message = this.props.t('asset.errorFileTooBig');
                    break;
                default:
                    message = `${this.props.t('validation.general')} : ${this.state.errorCode}`;
            }

            return <DisplayMessage message={message} messageType="error" />;
        } else if (this.state.saved) {
            return <DisplayMessage message={this.props.t('validation.general')} messageType="saved" />;
        }
        return <></>;
    }

    private async remove(id: string) {
        const url = `/api/surveys/${this.props.surveyId}/assets/${id}`;
        await fetchDelete(url);
        await this.loadAssets();
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(connect(mapStateToProps, undefined, undefined, { forwardRef: true })(SurveyEditDownloadAndDocuments));
